import {
  type UseQueryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import { QUERY_KEYS } from "./queryKeys";
import {
  fetchCuratorShowPromos,
  fetchShows,
  fetchShowDetails,
  IShowDetails,
} from "@src/apis/showApis";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { ICuratorShowPromo, IShowRecord } from "@src/types/show";
import { RoleType } from "@src/types/user";

export const useShowsQuery = (
  filters: {
    curatorId: number;
    roleType: RoleType;
    searchText: string;
    isMoodFactory?: boolean;
  },
  options?: Omit<
    UseQueryOptions<
      AxiosResponse<ApiResponse<IShowRecord[]>>,
      Error,
      AxiosResponse<ApiResponse<IShowRecord[]>>,
      [
        string,
        {
          curatorId: number;
          roleType: RoleType;
          searchText: string;
          isMoodFactory?: boolean;
        },
      ]
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<AxiosResponse<ApiResponse<IShowRecord[]>>, Error> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MY_SHOWS, filters],
    queryFn: () => fetchShows(filters),
    enabled: filters.curatorId !== undefined && filters.roleType !== undefined,
    ...options,
  });
};

export const useShowDetailsQuery = (
  documentId: string,
  options?: Omit<
    UseQueryOptions<IShowDetails, Error, IShowDetails, [string, string]>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<IShowDetails, Error> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_SHOW_DETAILS, documentId],
    queryFn: () => fetchShowDetails(documentId),
    enabled: !!documentId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// The curator's shows plus the airing each one is promoted from, used to
// prefill the Social Media Promo Instagram Story template. Cached per curator
// so reopening the story modal is instant.
export const useCuratorShowPromosQuery = (
  curatorDocumentId: string,
  options?: Omit<
    UseQueryOptions<
      ICuratorShowPromo[],
      Error,
      ICuratorShowPromo[],
      [string, string]
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<ICuratorShowPromo[], Error> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_CURATOR_SHOW_PROMOS, curatorDocumentId],
    queryFn: () => fetchCuratorShowPromos(curatorDocumentId),
    enabled: !!curatorDocumentId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};
